import { Play, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { splitCaptionByQuery } from "@/lib/caption-highlight";
import { slateCardClass } from "@/lib/card-styles";
import { typeNumericClass } from "@/lib/typography";
import {
    formatTimestamp,
    getPlatformDisplayName,
    supportsTimeSync,
} from "@/lib/video-types";
import type { VideoCaptionHit } from "@/lib/video-types";
interface CaptionClipListProps {
    hits: VideoCaptionHit[];
    /** What the reader typed — decides which words in each line light up. */
    query: string;
    /** The clip currently loaded in the player. */
    activeVideoRowId?: string | null;
    activeStart?: number | null;
    onSelect: (hit: VideoCaptionHit) => void;
    /**
     * A line above the caption, e.g. the topic word the clip was found for.
     * Kept as a render prop so the highlighting logic stays in this file —
     * a second caption renderer would drift from this one.
     */
    renderLead?: (hit: VideoCaptionHit) => React.ReactNode;
    /**
     * Per-row search term. The topic band looks words up one at a time, so each
     * line should light up *its own* word rather than one shared query.
     */
    queryFor?: (hit: VideoCaptionHit) => string;
    /** Rendered above the rows, inside the panel — a count, usually. */
    header?: React.ReactNode;
    /** Rendered under the rows, inside the panel — paging, usually. */
    footer?: React.ReactNode;
    className?: string;
}

/**
 * Render one transcript line with the searched words marked.
 *
 * The server sends the line as plain text and the browser decides what to
 * emphasise, so a transcript can never inject markup here — every piece is a
 * text node.
 */
function HighlightedLine({ text, query }: { text: string; query: string }) {
    const segments = splitCaptionByQuery(text, query);

    return (
        <>
            {segments.map((segment, index) =>
                segment.match ? (
                    <mark
                        key={index}
                        className="rounded-[3px] bg-amber-200/70 px-0.5 text-foreground dark:bg-amber-400/25 dark:text-amber-50"
                    >
                        {segment.text}
                    </mark>
                ) : (
                    <span key={index}>{segment.text}</span>
                ),
            )}
        </>
    );
}

/**
 * The result list for "in context" search: one row per matching **clip**.
 *
 * A row is a caption line, not a video, which is why it leads with the line and
 * the timestamp rather than a title — the title only says where the moment
 * came from.
 *
 * **One panel, divided rows.** It used to be a stack of bordered cards, which
 * made the twenty-row rail beside the player read as twenty competing surfaces
 * — and in the topic rail and the word dialog it was a card nested in a card.
 * The panel is the surface; a row is a row. Hover tints it, the open clip is
 * tinted and carries a filled play mark, and everything else stays quiet.
 */
export function CaptionClipList({
    hits,
    query,
    activeVideoRowId,
    activeStart,
    onSelect,
    renderLead,
    queryFor,
    header,
    footer,
    className,
}: CaptionClipListProps) {
    const { t } = useTranslation();

    return (
        <div className={cn(slateCardClass, "overflow-hidden", className)}>
            {header ? (
                <div className="border-b border-stone-200/70 px-3.5 py-2.5 dark:border-stone-800">
                    {header}
                </div>
            ) : null}

            <ul className="divide-y divide-stone-200/70 dark:divide-stone-800">
                {hits.map((hit) => {
                    const isActive =
                        hit.videoRowId === activeVideoRowId && hit.start === activeStart;
                    const rowQuery = queryFor?.(hit) ?? query;

                    // A hit is only worth loading into the player when the player can
                    // actually land on the line. TikTok, Instagram, Facebook and X
                    // embeds take no start parameter, so those rows are an outbound
                    // link to the source instead of a play button that would open at
                    // 0:00 and quietly mislead.
                    const canSeek = supportsTimeSync(hit.platform);

                    const rowClass = cn(
                        "group relative flex w-full items-start gap-3 px-3.5 py-3 text-left transition-colors",
                        "hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        "dark:hover:bg-stone-800/50",
                        isActive &&
                            "bg-primary/[0.055] hover:bg-primary/[0.08] dark:bg-primary/[0.14] dark:hover:bg-primary/[0.18]",
                    );

                    const body = (
                        <>
                            {/* One fixed box for both states: a thumbnail that
                                404s must leave the row's rhythm intact instead of
                                drawing a broken-image glyph. */}
                            <div
                                aria-hidden="true"
                                className="mt-0.5 aspect-video w-16 shrink-0 overflow-hidden rounded-md bg-stone-100 dark:bg-stone-800"
                            >
                                {hit.thumbnailUrl && (
                                    <img
                                        src={hit.thumbnailUrl}
                                        alt=""
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                        onError={(event) => {
                                            event.currentTarget.style.visibility = "hidden";
                                        }}
                                    />
                                )}
                            </div>

                            <div className="min-w-0 flex-1">
                                {renderLead?.(hit)}
                                {/* Title first: a clip is a moment *in a video*, and
                                    the spoken line reads as a fragment until you
                                    know which talk it came from. The transcript sits
                                    under it with the match still highlighted. */}
                                <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
                                    {hit.title}
                                </p>
                                <p className="mt-1 line-clamp-2 font-sans text-[13px] leading-snug text-muted-foreground">
                                    <HighlightedLine text={hit.text} query={rowQuery} />
                                </p>
                                <p className="mt-1.5 flex items-center gap-2">
                                    {/* The corpus is deliberately not labelled: a
                                        learner plays the clip the same way either
                                        side, so "Imported" was a badge that changed
                                        nothing they could act on. */}
                                    <span
                                        className={cn(
                                            typeNumericClass,
                                            "shrink-0 text-xs text-muted-foreground",
                                        )}
                                    >
                                        {formatTimestamp(hit.start)}–{formatTimestamp(hit.end)}
                                    </span>
                                </p>
                            </div>

                            {canSeek ? (
                                /* The row's only control. Quiet until pointed at,
                                   a filled blue disc once the clip is the one
                                   playing — the rail is an index, so the mark
                                   that says "this one" is the one strong thing. */
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        "mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                                        isActive
                                            ? "bg-primary-strong text-primary-foreground"
                                            : "text-stone-500 group-hover:bg-primary/10 group-hover:text-primary dark:text-stone-500 dark:group-hover:bg-primary/15",
                                    )}
                                >
                                    <Play className="h-3 w-3 fill-current" />
                                </span>
                            ) : (
                                <span
                                    aria-hidden="true"
                                    className="mt-0.5 inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-stone-200 px-2.5 text-[11px] text-muted-foreground transition-colors group-hover:border-primary/40 group-hover:text-primary dark:border-stone-800"
                                >
                                    <ExternalLink className="h-3 w-3" />
                                    {getPlatformDisplayName(hit.platform)}
                                </span>
                            )}
                        </>
                    );

                    return (
                        <li key={`${hit.source}-${hit.videoRowId}-${hit.start}`}>
                            {canSeek ? (
                                <button
                                    type="button"
                                    onClick={() => onSelect(hit)}
                                    aria-current={isActive ? "true" : undefined}
                                    className={rowClass}
                                >
                                    {body}
                                </button>
                            ) : (
                                // A real link, not a button with `window.open`: it keeps
                                // middle-click, "copy link" and the status-bar preview,
                                // and it is what the trailing chip promises.
                                <a
                                    href={hit.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={t("pages.videos.inContext.opensOn", {
                                        platform: getPlatformDisplayName(hit.platform),
                                        defaultValue:
                                            "Opens on {{platform}} — this app cannot jump to the moment there",
                                    })}
                                    className={rowClass}
                                >
                                    {body}
                                </a>
                            )}
                        </li>
                    );
                })}
            </ul>

            {footer ? (
                <div className="border-t border-stone-200/70 dark:border-stone-800">{footer}</div>
            ) : null}
        </div>
    );
}
